import type { SupportedCountry } from '@archipro-website/localisation';
import { DEFAULT_MARKET } from '@archipro-website/localisation';

/**
 * Minimal shape `articleAvailableCountries` reads from a page's localisation:
 * just its availability zones. Typing the parameter structurally (rather than
 * as the full `GetDirectoryPageMetaQuery['getDirectoryPageMeta']`) keeps the
 * call assignable from the raw query type, the route loader data, and Remix's
 * `SerializeFrom`-narrowed meta `data`, which can collapse to a subset of the
 * generated fields.
 */
export interface ArticleAvailabilityZoneSource {
    AvailabilityZones?: ReadonlyArray<{
        Country: { Code: string };
    } | null> | null;
}

/**
 * The market country codes an article is available in, read from its
 * availability zones. This is the single source of truth for which markets an
 * article belongs to, and drives both the `robots` noindex tag and the
 * hreflang alternates on the article page.
 *
 * Country codes are de-duplicated: a zone maps a region within a country, so an
 * article available in several regions of one market (e.g. two NZ zones) still
 * counts as a single market. Without this, a single-market article would look
 * multi-market and the canonical/hreflang home would be derived incorrectly.
 */
export const articleAvailableCountries = (
    localisation?: ArticleAvailabilityZoneSource | null,
): string[] => {
    const codes =
        localisation?.AvailabilityZones?.map(
            (zone) => zone?.Country.Code,
        ).filter((code): code is string => Boolean(code)) ?? [];
    return [...new Set(codes)];
};

/**
 * Whether an article should be indexable on the domain for `currentCountry`.
 *
 * An article is indexable only on the markets it is genuinely available in (its
 * availability zones), so an AU-only article served on the US domain is not
 * indexable. When the article has no zone data, or the current domain country
 * is unknown, it is treated as indexable to avoid noindexing on missing data.
 */
export const isArticleIndexableInCountry = (
    currentCountry: SupportedCountry | undefined,
    availableCountries: ReadonlyArray<string>,
): boolean => {
    if (!currentCountry || availableCountries.length === 0) {
        return true;
    }
    return availableCountries.includes(currentCountry);
};

/**
 * The market an article should be canonical to.
 *
 * The canonical must point at a market the article is genuinely available in.
 * The owning professional's country is preferred when the article is available
 * there, but editorial accounts can have e.g. `Country = NZ` while publishing
 * into AU + US zones, so the professional country is not always a valid home.
 * When it is not one of the article's availability zones, fall back to an
 * available zone market (preferring the default market) so the canonical never
 * lands on a host where the article does not exist. With no zone data, fall back
 * to the professional country.
 */
export const articleCanonicalCountry = (
    professionalCountry: string | null | undefined,
    availableCountries: ReadonlyArray<string>,
): string | null | undefined => {
    if (availableCountries.length === 0) {
        return professionalCountry;
    }
    if (
        professionalCountry &&
        availableCountries.includes(professionalCountry)
    ) {
        return professionalCountry;
    }
    if (availableCountries.includes(DEFAULT_MARKET)) {
        return DEFAULT_MARKET;
    }
    return availableCountries[0];
};
